import Highcharts from "highcharts";
import { HighchartsReact } from "highcharts-react-official";
import { useContext, useMemo, useRef } from "react";

import { WidgetContentContext } from "@/features/dashboard/components/widget-container-card";
import { useSelectedTimeRange } from "@/features/dashboard/store";
import { GraphProps } from "@/features/shared/widgets/graphs/graph.types";
import { useChartSizes } from "@/features/shared/widgets/graphs/hooks/useChartSizes";
import { LineGraphEdrLineAllOfColorEnum } from "@/lib/api";
import { NoDataOverlay } from "@/features/shared/widgets/graphs/no-data-label";
import { useLineGraphNoData } from "@/features/shared/widgets/graphs/hooks/use-line-graph-no-data";
import { usePrimaryUnit } from "@/features/shared/widgets/graphs/hooks/usePrimaryUnit";
import useLayoutStore from "@/store/useLayoutStore";

import baseOptions from "./base-options";
import { BLUE, GREEN, colorGenerator, getYAxisPerUnit, getFormattedValue } from "./helpers";

const Scatterplot = ({ data, configuration, options, currentTime }: GraphProps) => {
  const chartRef = useRef(null);
  const widgetContentContext = useContext(WidgetContentContext);
  const widgetContentRef = widgetContentContext.ref;

  const [isFullscreen] = useLayoutStore((s) => s.context.isFullscreen);

  const chartHeight = useChartSizes(chartRef, widgetContentRef, isFullscreen ?? false);

  const timeRange = useSelectedTimeRange();
  const timeStart = timeRange.timeStart;
  const timeEnd = timeRange.timeEnd;

  const primaryUnit = usePrimaryUnit(data);
  const primaryUnitType = useMemo(
    () => (primaryUnit?.type === "UNKNOWN" ? primaryUnit.symbol : primaryUnit?.type),
    [primaryUnit],
  );

  const chartData = useMemo(() => {
    if (!data || !configuration) return undefined;

    const yAxis = getYAxisPerUnit(primaryUnitType);
    yAxis[0] = {
      ...baseOptions.yAxis,
      ...yAxis[0],
      title: {
        text: primaryUnit?.symbol,
      },
    };

    // Create generators for lines to get the rights colors
    const greenGenerator = colorGenerator(GREEN);
    const blueGenerator = colorGenerator(BLUE);
    const getColors = (key: LineGraphEdrLineAllOfColorEnum) => {
      if (key === LineGraphEdrLineAllOfColorEnum.Green) {
        return greenGenerator.next().value;
      }
      if (key === LineGraphEdrLineAllOfColorEnum.Blue) {
        return blueGenerator.next().value;
      }

      return undefined;
    };

    if (options?.yAxis) {
      Array.isArray(options.yAxis)
        ? options.yAxis.forEach((axis, i) => (yAxis[i] = { ...yAxis[i], ...axis }))
        : (yAxis[0] = { ...yAxis[0], ...options.yAxis });
    }

    return {
      title: "",
      series: [
        ...(data.lines?.map((line) => {
          const lineConfig = configuration.lines.find((config) => config.id == line.id);
          const points =
            line.data?.datapoints.filter((point) => point.value !== null) ?? [];
          const color =
            points.length === 0
              ? "grey"
              : getColors(lineConfig?.color ?? LineGraphEdrLineAllOfColorEnum.Blue);

          return {
            type: "line",
            name:
              points.length === 0
                ? `${lineConfig?.title} [not available]`
                : lineConfig?.title,
            description: line.data?.parameter.description,
            data:
              line.data?.datapoints.map((datapoint) => [
                +new Date(datapoint.timestamp),
                getFormattedValue(datapoint.value, line.data?.parameter),
              ]) ?? [],
            tooltip: {
              valueSuffix: line.data?.parameter.unit.symbol,
            },
            color,
            marker: {
              enabled: true,
            },
            lineWidth: 0,
            states: {
              hover: {
                lineWidthPlus: 0,
              },
            },
          };
        }) ?? []),
      ],
      tooltip: {
        xDateFormat: "%A, %d-%m-%Y %H:%M",
      },
      xAxis: {
        type: "datetime",
        min: timeStart ? +timeStart : undefined,
        max: timeEnd ? +timeEnd : undefined,
        startOfWeek: 0,
        plotLines: currentTime
          ? [
              {
                id: "selectedTimeStart",
                color: "#FF0000",
                value: +currentTime,
              },
            ]
          : [],
      },
      yAxis,
      chart: {
        ...baseOptions.chart,
        height: chartHeight,
        ...options?.chart,
      },
      legend: {
        padding: 0,
      },
    } as Highcharts.Options;
  }, [data, configuration, chartHeight, primaryUnit]);

  const noData = useLineGraphNoData(data);

  return (
    <NoDataOverlay active={noData}>
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={chartData}
        containerProps={{
          style: {
            height: "100%",
            width: "100%",
          },
        }}
      />
    </NoDataOverlay>
  );
};

export default Scatterplot;
